import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FolderOpen, CheckCircle, Download } from "lucide-react";
import { usePageSeo, BASE_URL } from "@/hooks/useSeo";
import { toast } from "sonner";
import axios from "axios";
import Navigation from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FallstudienPage() {
  const [studies, setStudies] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/case-studies`).then(r => setStudies(r.data || [])).catch(() => {});
  }, []);

  usePageSeo({
    title: "Fallstudien | LAB336",
    description: "Entdecken Sie unsere Fallstudien: Digitale Customer Journeys, KI-gestützte Produktionsoptimierung und mehr. Konkrete Ergebnisse für Schweizer KMU.",
    canonical: `${BASE_URL}/fallstudien`,
  });

  const handleDownload = async (study) => {
    try {
      const res = await axios.get(`${API}/case-studies/${study.id}/download`);
      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
      } else {
        toast.info("PDF wird erstellt...");
      }
    } catch {
      toast.error("Download nicht verfügbar");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="fallstudien-page">
      <Navigation />
      <header className="bg-lab-bg-dark pt-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-12">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-lab-accent transition-colors text-sm mb-8" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Zurück zur Startseite
          </Link>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-white/10 flex items-center justify-center">
              <FolderOpen className="w-7 h-7 text-lab-accent" />
            </div>
            <div>
              <h1 className="font-playfair text-3xl lg:text-4xl font-semibold text-white">Fallstudien</h1>
              <p className="text-gray-400 mt-1">Konkrete Ergebnisse aus unseren Projekten</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-16">
        {studies.length === 0 ? (
          <p className="text-lab-text-muted text-center py-16">Noch keine Fallstudien veröffentlicht.</p>
        ) : (
          <div className="space-y-12">
            {studies.map((study, i) => (
              <motion.article
                key={study.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="grid md:grid-cols-2 gap-8 p-8 border border-lab-border bg-lab-bg-light"
                data-testid={`fallstudie-${study.id}`}
              >
                <div>
                  {study.image_url && (
                    <img src={study.image_url} alt={study.title} className="w-full h-48 object-cover mb-6" />
                  )}
                  <span className="text-xs text-lab-accent font-semibold tracking-widest uppercase">{study.client}</span>
                  <h2 className="font-playfair text-xl lg:text-2xl font-semibold text-lab-primary mt-2 mb-4">{study.title}</h2>
                  <p className="text-lab-text-muted text-sm leading-relaxed">{study.description}</p>
                </div>

                <div className="flex flex-col justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-lab-primary uppercase tracking-wider mb-4">Ergebnisse</h3>
                    <ul className="space-y-3">
                      {study.results?.map((result, j) => (
                        <li key={j} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                          <span className="text-lab-text text-sm">{result}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <button
                    onClick={() => handleDownload(study)}
                    className="mt-6 inline-flex items-center gap-2 bg-lab-primary text-white px-6 py-3 text-sm font-semibold hover:bg-lab-primary/90 transition-colors self-start"
                    data-testid={`download-${study.id}`}
                  >
                    <Download className="w-4 h-4" /> PDF herunterladen
                  </button>
                </div>
              </motion.article>
            ))}
          </div>
        )}

        <div className="mt-16 text-center">
          <p className="text-lab-text-muted mb-4">Bereit für Ihre eigene Erfolgsgeschichte?</p>
          <Link to="/termin" className="inline-block bg-lab-accent text-lab-primary px-8 py-3 text-sm font-semibold hover:bg-lab-accent/90 transition-colors" data-testid="fallstudien-cta">
            Beratungsgespräch vereinbaren
          </Link>
        </div>
      </main>

      <footer className="bg-lab-bg-dark border-t border-white/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-8 flex items-center justify-between">
          <p className="text-gray-500 text-sm">&copy; {new Date().getFullYear()} LAB 336</p>
          <Link to="/" className="text-gray-400 hover:text-lab-accent text-sm">Startseite</Link>
        </div>
      </footer>
    </div>
  );
}
